import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { ArrowRight, ArrowLeft, CheckCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import VoiceInput from '@/components/VoiceInput';
import { useTheme } from '@/contexts/ThemeContext';

const questions = [
  {
    id: 'mood',
    question: 'How was your week overall?',
    type: 'emoji',
    emoji: '🌤️',
    options: [
      { emoji: '🔥', label: 'Amazing', value: 'amazing' },
      { emoji: '😊', label: 'Good', value: 'good' },
      { emoji: '😐', label: 'Okay', value: 'okay' },
      { emoji: '😤', label: 'Rough', value: 'rough' },
      { emoji: '😩', label: 'Terrible', value: 'terrible' },
    ]
  },
  {
    id: 'energy',
    question: 'Rate your energy this week',
    type: 'rating',
    emoji: '⚡',
    scale: 5,
    low: 'Empty',
    high: 'Charged'
  },
  {
    id: 'activities',
    question: 'What did you do this week?',
    type: 'checklist',
    emoji: '✅',
    options: ['Learned something new', 'Led a meeting', 'Helped a coworker', 'Got feedback', 'Had a challenge', 'Made a connection', 'Used a new skill']
  },
  {
    id: 'highlight',
    question: 'Best part of your week?',
    type: 'text',
    placeholder: 'One moment you want to remember...',
    emoji: '⭐'
  },
  {
    id: 'challenge',
    question: 'Biggest challenge?',
    type: 'choice',
    emoji: '🤔',
    options: ['Time management', 'Communication', 'Staying motivated', 'Work-life balance', 'Skill gap', 'Nothing major']
  },
  {
    id: 'learned',
    question: 'What did you learn or get better at?',
    type: 'text',
    placeholder: 'A skill, a lesson, something about yourself...',
    emoji: '🧠'
  },
  {
    id: 'mentor_note',
    question: 'Anything for your lead monitor?',
    type: 'text',
    placeholder: 'Optional private note...',
    emoji: '💬',
    optional: true
  },
  {
    id: 'least_enjoyed',
    question: "What wasn't as fun this week?",
    type: 'text',
    placeholder: "What wasn't as fun or felt tedious?",
    emoji: '😕',
    optional: true
  },
  {
    id: 'questions_needs',
    question: 'Any questions or things you need?',
    type: 'text',
    placeholder: 'Is there anything you need help with or want to know more about?',
    emoji: '❓',
    optional: true
  }
];

const CheckInWizard = ({ user }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const currentQuestion = questions[step];
  const isLastStep = step === questions.length - 1;

  const handleNext = () => {
    const val = answers[currentQuestion.id];
    const isOptional = currentQuestion.optional;
    
    if (!isOptional && !val) {
      toast.error('Please answer this question');
      return;
    }
    if (!isOptional && currentQuestion.type === 'text' && (!val || val.trim().length < 5)) {
      toast.error('Please write a bit more');
      return;
    }
    if (isLastStep) {
      handleSubmit();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(answers)
      });

      if (response.ok) {
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.6 }
        });
        toast.success('Check-in submitted! +20 points 🎉');
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit check-in');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const { colors } = useTheme();

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Progress Bar */}
      <div style={{ backgroundColor: colors.surface }} className="shadow-sm">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-muted-foreground">
              Question {step + 1} of {questions.length}
            </p>
            <p className="text-sm font-semibold text-primary">
              {Math.round(((step + 1) / questions.length) * 100)}%
            </p>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((step + 1) / questions.length) * 100}%` }}
              transition={{ duration: 0.3 }}
              className="h-full bg-gradient-to-r from-primary via-purple-500 to-pink-500"
            />
          </div>
        </div>
      </div>

      {/* Question Card */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-2xl"
          >
            <div className="bg-white rounded-3xl shadow-floating p-8 md:p-12" style={{ backgroundColor: colors.card }}>
              <div className="text-center mb-8">
                <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 text-5xl" style={{ backgroundColor: colors.primary }}>
                  {currentQuestion.emoji}
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
                  {currentQuestion.question}
                </h2>
              </div>

              <Textarea
                data-testid={`checkin-${currentQuestion.id}`}
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => setAnswers({ ...answers, [currentQuestion.id]: e.target.value })}
                placeholder={currentQuestion.placeholder}
                className="min-h-[200px] text-lg rounded-2xl border-2 focus:border-primary resize-none"
                autoFocus
                style={{ display: currentQuestion.type === 'text' || !currentQuestion.type ? 'block' : 'none' }}
              />
              {(currentQuestion.type === 'text' || !currentQuestion.type) && (
                <div className="flex justify-center mt-3">
                  <VoiceInput size="lg" onTranscript={(text) => setAnswers(prev => ({...prev, [currentQuestion.id]: (prev[currentQuestion.id] || '') + (prev[currentQuestion.id] ? ' ' : '') + text}))} />
                </div>
              )}

              {/* Emoji Mood Selector */}
              {currentQuestion.type === 'emoji' && (
                <div className="flex justify-center gap-3 flex-wrap">
                  {currentQuestion.options.map(opt => (
                    <button key={opt.value} onClick={() => setAnswers({...answers, [currentQuestion.id]: opt.value})}
                      className={`flex flex-col items-center p-4 rounded-2xl transition-all ${answers[currentQuestion.id] === opt.value ? 'bg-orange-50 ring-2 ring-orange-400 scale-110' : 'bg-gray-50 hover:bg-gray-100'}`}>
                      <span className="text-4xl mb-1">{opt.emoji}</span>
                      <span className="text-xs font-bold text-gray-600">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Rating Scale */}
              {currentQuestion.type === 'rating' && (
                <div className="space-y-3">
                  <div className="flex justify-center gap-3">
                    {Array.from({length: currentQuestion.scale || 5}, (_, i) => i + 1).map(n => (
                      <button key={n} onClick={() => setAnswers({...answers, [currentQuestion.id]: String(n)})}
                        className={`w-14 h-14 rounded-2xl text-lg font-bold transition-all ${parseInt(answers[currentQuestion.id]) >= n ? 'bg-orange-500 text-white shadow-lg scale-110' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 px-2">
                    <span>{currentQuestion.low}</span><span>{currentQuestion.high}</span>
                  </div>
                </div>
              )}

              {/* Checklist */}
              {currentQuestion.type === 'checklist' && (
                <div className="space-y-2 max-w-md mx-auto">
                  {currentQuestion.options.map(opt => {
                    const sel = (answers[currentQuestion.id] || '').split(',').filter(Boolean);
                    const checked = sel.includes(opt);
                    return (
                      <button key={opt} onClick={() => {
                        const next = checked ? sel.filter(s => s !== opt) : [...sel, opt];
                        setAnswers({...answers, [currentQuestion.id]: next.join(',')});
                      }} className={`w-full p-3 rounded-xl text-left text-sm font-medium transition-all flex items-center gap-3 ${checked ? 'bg-orange-50 ring-1 ring-orange-300' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center ${checked ? 'bg-orange-500' : 'bg-white border border-gray-300'}`}>
                          {checked && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </div>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Choice */}
              {currentQuestion.type === 'choice' && (
                <div className="space-y-2 max-w-md mx-auto">
                  {currentQuestion.options.map(opt => (
                    <button key={opt} onClick={() => setAnswers({...answers, [currentQuestion.id]: opt})}
                      className={`w-full p-4 rounded-xl text-left text-sm font-medium transition-all ${answers[currentQuestion.id] === opt ? 'bg-orange-500 text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-4 mt-8">
                {step > 0 && (
                  <Button
                    data-testid="back-button"
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 h-14 rounded-full font-semibold"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                )}
                <Button
                  data-testid={isLastStep ? 'submit-button' : 'next-button'}
                  onClick={handleNext}
                  disabled={loading}
                  className={`flex-1 h-14 rounded-full bg-primary hover:bg-primary/90 text-white font-bold shadow-button hover:shadow-lg transition-all active:scale-95 ${step === 0 ? 'w-full' : ''}`}
                >
                  {loading ? (
                    'Submitting...'
                  ) : isLastStep ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Submit Check-in
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CheckInWizard;
